import { useEffect, useState } from 'react';

/**
 * useActiveSignupFlow — fail-safe reader for the Implify signup-flow copy switch.
 *
 * Paywall Feature Gate, Workstream 5 (website-em / PW-EM). Mirrors the app-repo
 * PW-3 hook (`client/src/hooks/useActiveSignupFlow.ts`) semantics but in CRA JS.
 *
 * Contract (A-PW-ROUTE — UNCONFIRMED BE CONTRACT):
 *   GET `${GATEWAY_BASE}/api/sbm/active-signup-flow`
 *     → { success: true, active_flow: 'paywall_first' | 'legacy' }
 *
 * Base URL comes from a NEW CRA env var `REACT_APP_IMPLIFY_GATEWAY_URL`
 * (build-time injected — CRA only exposes `REACT_APP_*`). This is DELIBERATELY a
 * new var: do NOT reuse `REACT_APP_BACKEND_URL`, which points at website-em's OWN
 * CMS/Python backend (see BlogListPage.jsx L8), not the Implify gateway.
 *
 * FAIL-SAFE: returns 'paywall_first' ONLY on an explicit
 * `success === true && active_flow === 'paywall_first'` response. Everything else —
 * unset env, network error, non-200, JSON parse error, `success !== true`, missing
 * or unknown `active_flow` — resolves to 'legacy'. The hook NEVER throws and NEVER
 * blocks render: it returns 'legacy' until (and unless) the endpoint confirms
 * paywall_first. So with the env unset or the endpoint absent, the marketing chip
 * shows today's copy — zero visible change until go-live.
 *
 * Optional ≤60s module-level cache so repeated mounts across agent pages issue at
 * most one request per window. Intentionally simple — no over-engineering.
 *
 * @returns {'legacy' | 'paywall_first'}
 */

// A-PW-ROUTE: unconfirmed BE contract — path assumed to be the Implify gateway's
// public signup-flow endpoint. If the confirmed contract differs, update here only.
const ACTIVE_FLOW_PATH = '/api/sbm/active-signup-flow';
const CACHE_TTL_MS = 60 * 1000;

// Module-level cache shared across all AgentPage mounts.
let cachedFlow = null;
let cachedAt = 0;

function getGatewayBase() {
  // CRA build-time env. Unset → '' → fetch fails safely → 'legacy'.
  return process.env.REACT_APP_IMPLIFY_GATEWAY_URL || '';
}

async function fetchActiveFlow() {
  const base = getGatewayBase();
  if (!base) {
    // Unset env — never reach the network; fail safe to legacy.
    return 'legacy';
  }
  try {
    const res = await fetch(`${base}${ACTIVE_FLOW_PATH}`);
    if (!res.ok) return 'legacy';
    const data = await res.json();
    if (data && data.success === true && data.active_flow === 'paywall_first') {
      return 'paywall_first';
    }
    return 'legacy';
  } catch {
    // Network / CORS / parse error — fail safe to legacy.
    return 'legacy';
  }
}

/**
 * Like useActiveSignupFlow but also reports `resolved` — whether the value is definitive yet
 * (fresh cache at mount, or a completed fetch). Gate flow-specific COPY on `resolved` so a cold
 * first load never flashes the legacy tail and then flips to paywall_first.
 * @returns {{ flow: 'legacy' | 'paywall_first', resolved: boolean }}
 */
export function useActiveSignupFlowState() {
  // Seed from a still-fresh cache (definitive); otherwise legacy placeholder, resolved=false.
  const fresh = cachedFlow && Date.now() - cachedAt < CACHE_TTL_MS;
  const [state, setState] = useState(
    fresh ? { flow: cachedFlow, resolved: true } : { flow: 'legacy', resolved: false }
  );

  useEffect(() => {
    let cancelled = false;

    // Fresh cache hit — no request needed.
    if (cachedFlow && Date.now() - cachedAt < CACHE_TTL_MS) {
      setState({ flow: cachedFlow, resolved: true });
      return () => {
        cancelled = true;
      };
    }

    fetchActiveFlow().then((flow) => {
      cachedFlow = flow;
      cachedAt = Date.now();
      if (!cancelled) setState({ flow, resolved: true });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

export function useActiveSignupFlow() {
  return useActiveSignupFlowState().flow;
}

export default useActiveSignupFlow;
